import { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { ChatExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import ChatMessageModal from '../../modals/chatmessage';

export type ChatMessagePayload = {
  text: string;
  nextNodeId: string;
};

type ChatMessageNodeAttributes = GenericNodeAttributes<
  ChatMessagePayload,
  ChatExtraData
>;
type ChatMessageNodeProps = GenericNodeProps<ChatMessagePayload, ChatExtraData>;

const translatePayload: ChatMessageNodeAttributes['translatePayload'] = (
  node,
) => {
  const { nextNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNodeId,
    },
  ];
};

const updateConnection: ChatMessageNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNodeId: connectionId };

  return {
    ...node,
    payload,
  };
};

export const ChatMessageNodeAttributes: ChatMessageNodeAttributes = {
  iconName: 'chat-plus',
  label: 'Chat message',
  color: hexStringToNumber(flowNodeColor.blue),
  translatePayload,
  updateConnection,
  NodeContent: ChatMessageNode,
  ActionModal: ChatMessageModal,
};

export function ChatMessageNode({ node }: ChatMessageNodeProps): JSX.Element {
  const { payload } = node;
  const { text } = payload;

  const sanitizedHtmlMessage = sanitizeHtml(text, {
    allowedTags: [],
  });

  return (
    <TextContent maxLines={2}>
      {useMemo(
        () => (
          <TextBreak>
            <Text weight="semibold" text={sanitizedHtmlMessage} />
          </TextBreak>
        ),
        [sanitizedHtmlMessage],
      )}
    </TextContent>
  );
}
